import { z } from 'zod';

// Use a structural type instead of TFunction to avoid brand incompatibility issues
// between different i18next installations in the monorepo
type TranslationFunction = {
  (key: string | string[], options?: unknown): string;
  (key: string | string[], defaultValue: string, options?: unknown): string;
};

export const viewTypes = ['table', 'swimlane', 'timeline'] as const;
export const viewTypeSchema = z.enum(viewTypes);

export type ViewType = z.infer<typeof viewTypeSchema>;

export const VIEW_TYPE_TRANSLATIONS = {
  swimlane: 'portfolio.viewTypes.swimlane',
  timeline: 'portfolio.viewTypes.timeline',
  table: 'portfolio.viewTypes.table',
} as const satisfies Record<ViewType, string>;

export const VIEW_TYPE_DESCRIPTION_TRANSLATIONS = {
  swimlane: 'roadmapping.create.roadmap.dialog.viewType.swimlane',
  timeline: 'roadmapping.create.roadmap.dialog.viewType.timeline',
  table: 'roadmapping.create.roadmap.dialog.viewType.table',
} as const satisfies Record<ViewType, string>;

// Until we support Timeline, we must explicitly limit the view types
export const SUPPORTED_VIEW_TYPES: ViewType[] = ['table', 'swimlane', 'timeline'] as const;

export const VIEW_MAX_NAME_LENGTH = 64;

export const viewDTOSchema = z.object({
  viewId: z.number().int(),
  organizationId: z.string().min(1),
  roadmapId: z.number().int(),
  name: z.string().max(VIEW_MAX_NAME_LENGTH).optional(),
  type: viewTypeSchema,
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type ViewDTO = z.infer<typeof viewDTOSchema>;

export function isAutoGeneratedName(name: string = ''): boolean {
  const autoGeneratedPattern = /^(Swimlane|Timeline|Table)\s+\(\d+\)$/;
  return autoGeneratedPattern.test(name);
}

// Use generated name, e.g., "Swimlane (3)" if the view has no name set
export function getViewDisplayName(view: ViewDTO, t: TranslationFunction): string {
  return view.name?.trim() || `${t(VIEW_TYPE_TRANSLATIONS[view.type])} (${view.viewId})`;
}

export function validateView(data: { name?: string | null }, ctx: z.RefinementCtx) {
  if (data.name && isAutoGeneratedName(data.name)) {
    ctx.addIssue({
      code: 'custom',
      message: 'View name cannot use auto-generated format. Please choose a different name.',
      path: ['name'],
    });
  }
}

export const viewInsertDTOSchema = z
  .object({
    roadmapId: z.number().int(),
    name: z.string().max(64).optional().nullable(),
    type: viewTypeSchema,
  })
  .strict()
  .superRefine(validateView);

export const viewUpdateDTOSchema = z
  .object({
    name: z.string().max(64).optional().nullable(),
  })
  .strict()
  .superRefine(validateView);

export type ViewInsertDTO = z.infer<typeof viewInsertDTOSchema>;
export type ViewUpdateDTO = z.infer<typeof viewUpdateDTOSchema>;
export type ViewUpsertDTO = ViewInsertDTO | ViewUpdateDTO;
